// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { CSSProperties } from 'react';

const colors = {
  border: '#E7E5E4',
  avatarActive: '#1e6b59'
} as const;

/** Matches reference .sidebar: 100px width, 24px padding, space-between, border, shadow */
export const sidebarRoot: CSSProperties = {
  position: 'relative',
  width: 100,
  minHeight: '100vh',
  background: '#fff',
  backdropFilter: 'blur(10px)',
  padding: '24px 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRight: `1px solid ${colors.border}`,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
  boxSizing: 'border-box'
};

/** Apply when contact popover is open so popover stacks above main content */
export const sidebarRootPopoverOpen: CSSProperties = {
  zIndex: 10000
};

/** Logo area – reference uses IconButton with no circular bg */
export const logoWrapper: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

/** Matches reference .nav: gap 2px, flex 1, margin-top 16px, full width */
export const navList: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  flex: 1,
  justifyContent: 'flex-start',
  marginTop: 16,
  width: '100%',
  alignItems: 'center'
};

/** Matches reference .nav-btn: transparent, padding 8px, color #e0e0e0, filter, transition */
export const navItemBase: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 8,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  color: '#e0e0e0',
  filter: 'brightness(0) saturate(100%)',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

/** Matches reference .nav-btn.active: background rgba(206, 206, 206, 0.05) */
export const navItemActive: CSSProperties = {
  ...navItemBase,
  background: 'rgba(206, 206, 206, 0.05)'
};

/** Matches reference .sidebar-profile: margin-top 24px */
export const avatarWrapper: CSSProperties = {
  position: 'relative',
  flexShrink: 0,
  marginTop: 24
};

/** Matches reference .avatar-initial: 40x40, #1e6b59, white, font 1.2rem */
export const avatarBottom: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  background: colors.avatarActive,
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 500,
  fontSize: '1.2rem',
  border: 'none',
  cursor: 'pointer',
  padding: 0
};

export const popover: CSSProperties = {
  position: 'absolute',
  bottom: '-12px',
  left: '118PX',
  transform: 'translateX(-50%)',
  marginBottom: 8,
  minWidth: 140,
  backgroundColor: '#FFFFFF',
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
  padding: '6px 0',
  zIndex: 1000
};

export const popoverItem: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '10px 16px',
  border: 'none',
  background: 'none',
  fontSize: 14,
  fontWeight: 500,
  color: '#1C1917',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  borderRadius: 6
};

/** Contact popover: appears to the right of the phone icon */
export const contactPopoverWrapper: CSSProperties = {
  position: 'relative',
  width: '100%',
  display: 'flex',
  justifyContent: 'center'
};

export const contactPopover: CSSProperties = {
  position: 'absolute',
  left: 'calc(100% + 8px)',
  top: 0,
  minWidth: 320,
  width: 320,
  backgroundColor: '#FFFFFF',
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
  padding: '16px 20px',
  zIndex: 99999,
  boxSizing: 'border-box'
};

export const contactPopoverClose: CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 12,
  width: 24,
  height: 24,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: '#78716C',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'G42 Sans',
  fontSize: 24,
  lineHeight: 1
};

export const contactPopoverTitle: CSSProperties = {
  margin: '0 28px 12px 0',
  fontSize: 14,
  fontWeight: 500,
  color: '#1C1917'
};

export const contactPopoverLine: CSSProperties = {
  marginBottom: 8,
  fontSize: 14,
  color: '#2a312b',
  fontWeight: 500
};

export const contactPopoverMailto: CSSProperties = {
  color: '#0D9488',
  textDecoration: 'underline',
  cursor: 'pointer'
};
